import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { ArtistCard, cardGrid } from "../components/Cards";
import { Pagination } from "../components/Pagination";
import { LetterBar } from "../components/LetterBar";
import { useSettings } from "../context/SettingsContext";
import { GENRES } from "../lib/constants";
import { useTerms } from "../lib/useTerms";

const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];
const PTYPES = ["", "Solo artist", "Band", "DJ", "Karaoke", "Comedy"];

export default function Artists() {
  const { settings } = useSettings();
  const cs = settings?.card_styles?.artists || "default";
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: sp.get("q") || "", state: "", city: "", genre: "", artist_type: "", booking_available: false, letter: "" });
  const genres = useTerms("artist", "genre", GENRES);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 12 });
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    const { data } = await api.get(`/artists?${p.toString()}`);
    setItems(data.items); setMeta(data); setLoading(false);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [f, page]);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };
  const sel = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-rose-500";

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10" data-testid="artists-page">
      <p className="overline mb-2">Bands, DJs & performers</p>
      <h1 className="font-display font-extrabold text-4xl md:text-5xl mb-8">Artist Directory</h1>
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-20 bg-[#111] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal size={16} /> Filters</div>
            <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search artists" className={sel} data-testid="afilter-q" />
            <select value={f.genre} onChange={(e) => set("genre", e.target.value)} className={sel} data-testid="afilter-genre"><option value="">All Genres</option>{genres.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <select value={f.state} onChange={(e) => set("state", e.target.value)} className={sel} data-testid="afilter-state">{STATES.map((s) => <option key={s} value={s}>{s || "All States"}</option>)}</select>
            <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Home city" className={sel} data-testid="afilter-city" />
            <select value={f.artist_type} onChange={(e) => set("artist_type", e.target.value)} className={sel} data-testid="afilter-ptype">{PTYPES.map((s) => <option key={s} value={s}>{s || "All Types"}</option>)}</select>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.booking_available} onChange={(e) => set("booking_available", e.target.checked)} data-testid="afilter-booking" /> Booking available</label>
          </div>
        </aside>
        <div className="lg:col-span-9 min-w-0">
          <LetterBar value={f.letter} onChange={(v) => set("letter", v)} />
          <p className="text-sm text-zinc-400 mb-5" data-testid="artists-count">{loading ? "Loading..." : `Showing ${items.length} of ${meta.total} artist${meta.total === 1 ? "" : "s"}${f.letter ? ` under ${f.letter.toUpperCase()}` : ""}`}</p>
          <div className={`grid gap-5 ${cardGrid(cs)}`}>{items.map((a) => <ArtistCard key={a.id} a={a} style={cs} />)}</div>
          {!loading && items.length === 0 && (
            <div className="text-center py-20 space-y-3" data-testid="artists-empty">
              <p className="text-zinc-300 font-semibold">No artists found{f.letter ? ` under ${f.letter.toUpperCase()}` : ""}.</p>
              <p className="text-zinc-500 text-sm">Try clearing your filters or browse all artists — or submit your own artist profile!</p>
              <button onClick={() => { setF({ q: "", state: "", city: "", genre: "", artist_type: "", booking_available: false, letter: "" }); setPage(1); }}
                className="text-sm bg-rose-500 hover:bg-rose-600 font-bold px-5 py-2 rounded-lg" data-testid="artists-clear-filters">Clear all filters</button>
            </div>
          )}
          <Pagination page={page} pages={meta.pages} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
